import React, { Fragment } from 'react';
import gql from 'graphql-tag';
import AutoComplete from './AutoComplete';
import Spinner from '../Spinner';
import { Query } from '../../reactApolloCompat';
import OptionalTextField from './OptionalTextField';
import { validationHelper } from '../../util/validation';
import Grid from './Grid';
import Label from './Label';
import ValidationPlaceholder from './ValidationPlaceholder';

const LeadChannelAutoComplete = ({ children, className, name, form, label, limit = 25, ...props }) => {
    const labelText = label || 'Enquirer Lead Information';
    const hasChannel = form.getField(name + '.ChannelID') > 0;
    const hasSource = form.getField(name + '.ChannelOptionID') > 0 || !!form.getField(name + '.SourceName');
    const valResult = !hasChannel
        ? validationHelper.required('Please ask about how they found us.')
        : !hasSource
        ? validationHelper.suggested('Please ask for their source.')
        : validationHelper.ok();

    return (
        <Grid bucket>
            <ValidationPlaceholder validationResult={valResult}>
                <Grid item xs={12}>
                    <Label text={labelText} />
                </Grid>
                <Query query={readAllChannelsQuery} variables={{ limit }}>
                    {({ data, loading, error }) => {
                        if (loading)
                            return (
                                <Fragment>
                                    <Spinner />
                                    Loading Lead Channels...
                                </Fragment>
                            );

                        if (error) return <Fragment>Failed to load channel data</Fragment>;
                        const validSources = data.readLeadChannels?.nodes.map(obj => ({
                            label: obj.Title,
                            value: obj.ID,
                            disabled: obj.IsRetired
                        }));
                        const sourceName = name + '.ChannelID';
                        const sourceOptionName = name + '.ChannelOptionID';
                        const hasChannelID = form.getField(sourceName);
                        const hasChannelOptionID = form.getField(sourceOptionName);
                        const validSource = data.readLeadChannels?.nodes.find(obj => obj.ID === hasChannelID);
                        const validSourceOptions =
                            validSource?.Options.map(obj => ({
                                label: obj.Title,
                                value: obj.ID,
                                disabled: obj.IsRetired
                            })) || [];
                        !!validSourceOptions.length &&
                            validSourceOptions.push({
                                label: 'Other, see comment...',
                                value: '0'
                            });
                        const optionsPrompt = (validSource && validSource.Prompt) || 'What was their source?';
                        const placeholder = !validSource
                            ? 'Please ask first how they found us'
                            : 'Please ask for their source';

                        const onChangeChannel = result => {
                            const newChannelID = result?.value;
                            if (!(newChannelID > 0)) {
                                form.setField({ [sourceOptionName]: null });
                            } else {
                                const validOldSource = data.readLeadChannels?.nodes.find(
                                    obj => obj.ID === newChannelID
                                );
                                if (!validOldSource?.Options.find(obj => obj.ID === hasChannelOptionID)) {
                                    form.setField({ [sourceOptionName]: null });
                                }
                            }
                        };

                        return (
                            <>
                                <Grid item xs={12} sm={6} lg={12} xl={6}>
                                    <AutoComplete
                                        label="How did they find us?"
                                        placeholder="Choose or start typing..."
                                        form={form}
                                        name={sourceName}
                                        className={className}
                                        options={validSources}
                                        onChange={onChangeChannel}
                                        clearOnSelect={false}
                                        limit={limit}
                                        minChars={0}
                                    />
                                </Grid>
                                <Grid item xs={12} sm={6} lg={12} xl={6}>
                                    <AutoComplete
                                        label={optionsPrompt}
                                        placeholder={placeholder}
                                        disabled={!validSource || !validSourceOptions.length > 1}
                                        form={form}
                                        name={sourceOptionName}
                                        className={className}
                                        options={validSourceOptions}
                                        clearOnSelect={false}
                                        limit={limit}
                                        minChars={0}
                                    />
                                </Grid>
                                <Grid item xs={12}>
                                    <OptionalTextField
                                        buttonlabel="+ Add Comment"
                                        textfieldlabel="Comments about the lead source"
                                        form={form}
                                        name={name + '.SourceName'}
                                    />
                                </Grid>
                            </>
                        );
                    }}
                </Query>
            </ValidationPlaceholder>
        </Grid>
    );
};
const readAllChannelsQuery = gql`
    query LeadChannels($limit: Int, $offset: Int) {
        readLeadChannels(limit: $limit, offset: $offset) {
            nodes {
                ID
                Title
                Prompt
                IsRetired
                Options {
                    ID
                    Title
                    IsRetired
                }
            }
            pageInfo {
                totalCount
            }
        }
    }
`;

export default LeadChannelAutoComplete;
